import React, { ChangeEvent, useRef } from 'react';
import { IconSearch, FormInput, LoadingIndicator, FormInputIcon, IconX } from '@spotify-internal/encore-web';
import styled from 'styled-components';

import { useTranslation } from 'providers/translations/TranslationProvider';

interface SearchInputProps {
  setSearchQuery: (searchQuery: string) => void;
  searchQuery: string;
  isSearching: boolean;
  clearSearch: () => void;
}

const StyledIconX = styled(IconX)`
  &:hover {
    cursor: pointer;
  }
`;

const StyledFormInputIcon = styled(FormInputIcon)`
  > div:first-child {
    [dir='rtl'] & {
      right: 12px;
      left: auto;
    }
    [dir='ltr'] & {
      right: auto;
      left: 12px;
    }
  }
`;

// Screens larger than this won't have trouble fitting in results and keyboards
const isMobile = () => window.matchMedia('(max-width: 480px)').matches;

const SearchInput = ({ setSearchQuery, searchQuery, isSearching, clearSearch }: SearchInputProps) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const alignToTop = () => {
    // iOS safari centers inputs on focus
    // This is a workaround to align it on top
    setTimeout(() => {
      if (searchInputRef.current && isMobile()) {
        searchInputRef.current.scrollIntoView();
      }
    }, 100);
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchInputRef.current?.blur();
    }
  };

  let trailingIcon;

  if (searchQuery !== '' && !isSearching) {
    trailingIcon = (
      <StyledIconX
        onClick={clearSearch}
        data-testid="iconClearHide"
        role="button"
        aria-label={t('clear_search')}
        tabIndex={0}
      />
    );
  } else if (isSearching) {
    trailingIcon = <LoadingIndicator indicatorSize={LoadingIndicator.sm} />;
  }

  return (
    <>
      <StyledFormInputIcon
        className="encore-inverted-light-set"
        iconLeading={<IconSearch aria-hidden="true" />}
        iconTrailing={trailingIcon}
      >
        <FormInput
          ref={searchInputRef}
          type="text"
          aria-label={t('search_placeholder')}
          placeholder={t('search_placeholder')}
          autoComplete="off"
          value={searchQuery}
          onFocus={alignToTop}
          onKeyUp={onKeyUp}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchQuery(event.target.value)}
          data-testid="searchContainerInput"
        />
      </StyledFormInputIcon>
    </>
  );
};

export default SearchInput;
